import React, { useState, useCallback } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import {
  UserPlus, Loader2, MapPin, Star,
  Sun, Moon, AlertCircle, CalendarX, Search, X
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

interface Musician {
  id: string;
  name: string;
  tier: string | null;
  region: string;
  composite_score: number | null;
  availability_weekdays: boolean | null;
  availability_weekends: boolean | null;
  primary_instrument: string | null;
  instruments: string[];
  hourly_rate: number | null;
}

interface ExistingAssignment {
  id: string;
  musician_id: string | null;
  instrument: string;
  event_index: number;
  status: string;
  source: string;
  musician?: { name: string; region: string } | null;
}

interface InstrumentHoverCardProps {
  instrument: string;
  inquiryId: string;
  eventIndex: number;
  inquiryRegion?: string;
  eventDate?: string;
  existingAssignment?: ExistingAssignment | null;
  onMusicianAssigned?: () => void;
  children: React.ReactNode;
}

const TIER_ORDER: Record<string, number> = { A: 0, B: 1, C: 2 };
const TIER_COLORS: Record<string, string> = {
  A: "bg-green-600 text-white",
  B: "bg-amber-500 text-white",
  C: "bg-muted text-muted-foreground",
};

export function InstrumentHoverCard({
  instrument,
  inquiryId,
  eventIndex,
  inquiryRegion,
  eventDate,
  existingAssignment,
  onMusicianAssigned,
  children,
}: InstrumentHoverCardProps) {
  const [musicians, setMusicians] = useState<Musician[]>([]);
  const [bookedMusicianIds, setBookedMusicianIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [assigning, setAssigning] = useState<string | null>(null);
  const [unassigning, setUnassigning] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  // Parse event date to YYYY-MM-DD for DB queries
  const parsedEventDate = eventDate ? (() => {
    try {
      const d = new Date(eventDate);
      if (isNaN(d.getTime())) return null;
      return d.toISOString().split("T")[0];
    } catch { return null; }
  })() : null;

  const fetchMusicians = useCallback(async () => {
    if (loaded) return;
    setLoading(true);
    try {
      // Fetch musicians and existing bookings in parallel
      const [musiciansRes, bookingsRes] = await Promise.all([
        supabase
          .from("musicians")
          .select("id, name, tier, region, composite_score, availability_weekdays, availability_weekends, primary_instrument, instruments, hourly_rate")
          .eq("is_active", true)
          .order("composite_score", { ascending: false }),
        // Fetch all non-cancelled assignments for this event date
        parsedEventDate
          ? supabase
              .from("musician_assignments")
              .select("musician_id")
              .eq("event_date", parsedEventDate)
              .neq("status", "cancelled")
          : Promise.resolve({ data: [], error: null }),
      ]);

      if (musiciansRes.error) throw musiciansRes.error;

      // Build set of booked musician IDs for this date
      const booked = new Set<string>(
        (bookingsRes.data || []).map((a: any) => a.musician_id)
      );
      setBookedMusicianIds(booked);

      // Client-side filter: match instrument name loosely
      const lowerInst = instrument.toLowerCase();
      const filtered = (musiciansRes.data || []).filter((m) => {
        const matchesArray = m.instruments?.some(
          (i: string) => i.toLowerCase().includes(lowerInst) || lowerInst.includes(i.toLowerCase())
        );
        const matchesPrimary = m.primary_instrument?.toLowerCase().includes(lowerInst) ||
          lowerInst.includes(m.primary_instrument?.toLowerCase() || "");
        return matchesArray || matchesPrimary;
      });

      // Determine if event is on a weekend
      const isWeekend = eventDate ? [0, 6].includes(new Date(eventDate).getDay()) : null;

      const isAvailableForEvent = (m: Musician): boolean => {
        if (isWeekend === null) return true;
        return isWeekend ? !!m.availability_weekends : !!m.availability_weekdays;
      };

      // Sort: not-booked first, then available, then region match, then tier, then score
      const lowerRegion = inquiryRegion?.toLowerCase();
      filtered.sort((a, b) => {
        // 0. Non-booked first
        const aBooked = booked.has(a.id) ? 1 : 0;
        const bBooked = booked.has(b.id) ? 1 : 0;
        if (aBooked !== bBooked) return aBooked - bBooked;
        // 1. Available musicians first
        const aAvail = isAvailableForEvent(a) ? 0 : 1;
        const bAvail = isAvailableForEvent(b) ? 0 : 1;
        if (aAvail !== bAvail) return aAvail - bAvail;
        // 2. Region match
        if (lowerRegion) {
          const aMatch = a.region.toLowerCase() === lowerRegion ? 0 : 1;
          const bMatch = b.region.toLowerCase() === lowerRegion ? 0 : 1;
          if (aMatch !== bMatch) return aMatch - bMatch;
        }
        // 3. Then tier
        const tierA = TIER_ORDER[a.tier || "C"] ?? 3;
        const tierB = TIER_ORDER[b.tier || "C"] ?? 3;
        if (tierA !== tierB) return tierA - tierB;
        // 4. Then composite score
        return (b.composite_score ?? 0) - (a.composite_score ?? 0);
      });

      setMusicians(filtered);
      setLoaded(true);
    } catch (err) {
      console.error("Failed to fetch musicians:", err);
    } finally {
      setLoading(false);
    }
  }, [instrument, inquiryRegion, eventDate, parsedEventDate, loaded]);

  // Use a ref to keep the card open during parent re-renders
  const keepOpenRef = React.useRef(false);

  const handleAssign = async (musician: Musician) => {
    if (bookedMusicianIds.has(musician.id)) {
      toast.error(`${musician.name} is already booked on this date`);
      return;
    }

    setAssigning(musician.id);
    try {
      const { error } = await supabase.from("musician_assignments").insert({
        musician_id: musician.id,
        inquiry_id: inquiryId,
        event_index: eventIndex,
        instrument: instrument,
        event_date: parsedEventDate,
        status: "pending",
      });
      if (error) {
        if (error.code === "23505") {
          toast.error(`${musician.name} is already booked on this date`, {
            description: "Cannot double-book a musician for the same date.",
          });
          return;
        }
        throw error;
      }
      toast.success(`${musician.name} assigned to ${instrument}`);
      setBookedMusicianIds(prev => new Set([...prev, musician.id]));
      keepOpenRef.current = true;
      onMusicianAssigned?.();
      // Re-force open after the parent re-render cycle
      requestAnimationFrame(() => {
        setIsOpen(true);
        keepOpenRef.current = false;
      });
    } catch (err: any) {
      toast.error("Failed to assign musician", { description: err.message });
    } finally {
      setAssigning(null);
    }
  };

  const handleAssignBroker = async () => {
    setAssigning("broker");
    try {
      const { error } = await supabase.from("musician_assignments").insert({
        inquiry_id: inquiryId,
        event_index: eventIndex,
        instrument: instrument,
        event_date: parsedEventDate,
        status: "broker_sourcing",
        source: "broker",
        notes: "Sourcing from external broker",
      });
      if (error) throw error;
      toast.success(`Broker sourcing started for ${instrument}`);
      keepOpenRef.current = true;
      onMusicianAssigned?.();
      requestAnimationFrame(() => {
        setIsOpen(true);
        keepOpenRef.current = false;
      });
    } catch (err: any) {
      toast.error("Failed to create broker assignment", { description: err.message });
    } finally {
      setAssigning(null);
    }
  };

  const handleUnassign = async () => {
    if (!existingAssignment) return;
    setUnassigning(true);
    try {
      const { error } = await supabase
        .from("musician_assignments")
        .delete()
        .eq("id", existingAssignment.id);
      if (error) throw error;
      toast.success(`Unassigned ${instrument}`);
      keepOpenRef.current = true;
      onMusicianAssigned?.();
      requestAnimationFrame(() => {
        setIsOpen(true);
        keepOpenRef.current = false;
      });
    } catch (err: any) {
      toast.error("Failed to unassign", { description: err.message });
    } finally {
      setUnassigning(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    // Don't close if we're in the middle of an assignment action
    if (!open && keepOpenRef.current) return;
    setIsOpen(open);
    if (open && !loaded && !existingAssignment) {
      fetchMusicians();
    }
  };

  return (
    <HoverCard openDelay={200} closeDelay={150} open={isOpen} onOpenChange={handleOpenChange}>
      <HoverCardTrigger asChild>
        {children}
      </HoverCardTrigger>
      <HoverCardContent
        className="w-80 p-0 bg-popover border shadow-lg z-50"
        side="bottom"
        align="start"
        sideOffset={6}
      >
        <div className="p-2.5 border-b bg-muted/30">
          <p className="text-xs font-medium flex items-center gap-1.5">
            <Star className="h-3.5 w-3.5 text-amber-500" />
            {instrument}
          </p>
          <div className="flex items-center gap-2 mt-0.5">
            {inquiryRegion && (
              <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                <MapPin className="h-2.5 w-2.5" />
                {inquiryRegion}
              </span>
            )}
            {parsedEventDate && (
              <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                <AlertCircle className="h-2.5 w-2.5" />
                {new Date(parsedEventDate + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
              </span>
            )}
          </div>
        </div>

        {/* Already assigned — show assigned musician with unassign */}
        {existingAssignment ? (
          <div className="p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 min-w-0">
                <UserPlus className="h-3.5 w-3.5 text-primary shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs font-medium truncate">
                    {existingAssignment.source === "broker"
                      ? "Broker Sourcing"
                      : existingAssignment.musician?.name || "Unknown"}
                  </p>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <Badge variant="outline" className="text-[9px] h-4 px-1">
                      {existingAssignment.status}
                    </Badge>
                    {existingAssignment.musician?.region && (
                      <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                        <MapPin className="h-2.5 w-2.5" />
                        {existingAssignment.musician.region}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <Button
                size="sm"
                variant="ghost"
                className="h-6 px-2 text-[10px] gap-1 text-destructive hover:bg-destructive hover:text-destructive-foreground shrink-0"
                onClick={handleUnassign}
                disabled={unassigning}
              >
                {unassigning ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <>
                    <X className="h-3 w-3" />
                    Unassign
                  </>
                )}
              </Button>
            </div>
          </div>
        ) : (
          <>
            {loading ? (
              <div className="p-6 flex items-center justify-center">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            ) : musicians.length === 0 ? (
              <div className="p-4 text-center">
                <p className="text-xs text-muted-foreground">No musicians available for this instrument</p>
              </div>
            ) : (
              <ScrollArea className="max-h-64">
                <div className="divide-y">
                  {musicians.map((m) => {
                    const isRegionMatch = inquiryRegion && m.region.toLowerCase() === inquiryRegion.toLowerCase();
                    const isWeekend = eventDate ? [0, 6].includes(new Date(eventDate).getDay()) : null;
                    const isAvailable = isWeekend === null ? true : isWeekend ? !!m.availability_weekends : !!m.availability_weekdays;
                    const isBooked = bookedMusicianIds.has(m.id);
                    return (
                      <div
                        key={m.id}
                        className={cn(
                          "px-2.5 py-2 flex items-center gap-2 hover:bg-accent/50 transition-colors",
                          isRegionMatch && !isBooked && "bg-primary/10 border-l-2 border-l-primary",
                          isBooked && "opacity-40 bg-destructive/5 border-l-2 border-l-destructive/40",
                          !isAvailable && !isBooked && "opacity-50"
                        )}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className={cn("text-xs font-medium truncate", isBooked && "line-through")}>{m.name}</span>
                            <Badge
                              className={cn(
                                "text-[9px] h-4 px-1.5 rounded-sm font-bold",
                                TIER_COLORS[m.tier || "C"] || TIER_COLORS.C
                              )}
                            >
                              {m.tier || "—"}
                            </Badge>
                            {isBooked && (
                              <Badge variant="outline" className="text-[9px] h-4 px-1 text-destructive border-destructive/40 gap-0.5">
                                <CalendarX className="h-2.5 w-2.5" />
                                Booked
                              </Badge>
                            )}
                            {!isAvailable && !isBooked && (
                              <Badge variant="outline" className="text-[9px] h-4 px-1 text-destructive border-destructive/30">
                                Unavailable
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className={cn(
                              "text-[10px] flex items-center gap-0.5",
                              isRegionMatch ? "text-primary font-medium" : "text-muted-foreground"
                            )}>
                              <MapPin className="h-2.5 w-2.5" />
                              {m.region}
                            </span>
                            {m.composite_score != null && (
                              <span className="text-[10px] text-muted-foreground font-mono">
                                {Math.round(m.composite_score)}/100
                              </span>
                            )}
                            <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                              {m.availability_weekdays && <Sun className="h-2.5 w-2.5 text-amber-500" />}
                              {m.availability_weekends && <Moon className="h-2.5 w-2.5 text-indigo-500" />}
                            </span>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 px-2 text-[10px] gap-1 shrink-0 hover:bg-primary hover:text-primary-foreground"
                          onClick={() => handleAssign(m)}
                          disabled={assigning === m.id || isBooked}
                        >
                          {assigning === m.id ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : isBooked ? (
                            <CalendarX className="h-3 w-3 text-destructive" />
                          ) : (
                            <>
                              <UserPlus className="h-3 w-3" />
                              Assign
                            </>
                          )}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            )}

            <div className="p-2 border-t bg-muted/20 flex items-center justify-between">
              <span className="text-[10px] text-muted-foreground">
                {musicians.length - bookedMusicianIds.size} of {musicians.length} available
              </span>
              <Button
                size="sm"
                variant="ghost"
                className="h-5 px-2 text-[10px] gap-1 text-muted-foreground hover:text-foreground"
                onClick={() => handleAssignBroker()}
                disabled={assigning === "broker"}
              >
                {assigning === "broker" ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <>
                    <Search className="h-2.5 w-2.5" />
                    Source from broker
                  </>
                )}
              </Button>
            </div>
          </>
        )}
      </HoverCardContent>
    </HoverCard>
  );
}
